import { Button, Spin, Typography } from 'antd/lib';
import { Tooltip } from '@atvlibs/atvdesignsystem';
import styles from './CameraList.module.css';
import { Icon } from 'components/UI/Icon/Icon';
import { IconNames } from 'components/UI/Icon/icons';
import { DeviceStatusEnum } from 'api/api_code';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import { Translate } from 'next-translate';
import { memo } from 'react';
import clsx from 'clsx';

const { Text } = Typography;
export enum FirmwareStatus {
	INSTALL = 'INSTALL',
	INSTALLING = 'INSTALLING',
	UPGRADE = 'UPGRADE',
	UPGRADING = 'UPGRADING',
	LATEST = 'LATEST',
	INSTALL_FAILED = 'INSTALL_FAILED',
	UPGRADE_FAILED = 'UPGRADE_FAILED',
}

interface FirmwareFieldProps {
	version: string;
	newVersion: string;
	action: (event) => void;
	firmwareStatus: FirmwareStatus;
	deviceStatus: DeviceStatusEnum;
	translate: Translate;
	disable?: boolean;
	noTextStyle?: boolean;
	actionInProgress?: boolean;
	isServerless?: boolean;
}
const FirmwareField = memo(
	({
		version,
		newVersion,
		action,
		firmwareStatus,
		deviceStatus,
		translate,
		disable = false,
		noTextStyle = false,
		actionInProgress = false,
		isServerless = false,
	}: FirmwareFieldProps) => {
		const isCameraUnavailable =
			firmwareStatus === FirmwareStatus.INSTALLING ||
			firmwareStatus === FirmwareStatus.UPGRADING;

		const firmwareActionAvailable =
			firmwareStatus === FirmwareStatus.INSTALL ||
			firmwareStatus === FirmwareStatus.UPGRADE;

		const noFirmwareInstalled = firmwareStatus === FirmwareStatus.INSTALL;

		const upgradeFailed = firmwareStatus === FirmwareStatus.UPGRADE_FAILED;
		const installFailed = firmwareStatus === FirmwareStatus.INSTALL_FAILED;

		const firmwareButtonProps = {
			disabled: isCameraUnavailable || disable || actionInProgress,
			className: styles.upgrade,
		};
		const transformVersion = (version: string) => {
			return version?.includes('commit') ? version?.split('-')[0] : version;
		};
		const simplifiedVersion = transformVersion(version);

		const extractCommitNumber = (version: string): number | null => {
			if (!version?.includes('commit')) return null;
			const match = version.match(/-commit-(\d+)/);
			return match ? parseInt(match[1], 10) : null;
		};

		const compareVersions = (v1: string, v2: string): number => {
			const base1 = transformVersion(v1);
			const base2 = transformVersion(v2);

			const parts1 = (base1 ?? '').split('.').map(Number);
			const parts2 = (base2 ?? '').split('.').map(Number);
			const len = Math.max(parts1.length, parts2.length);

			for (let i = 0; i < len; i++) {
				const a = parts1[i] ?? 0;
				const b = parts2[i] ?? 0;
				if (a !== b) return a - b;
			}

			// If base versions are equal, compare commit numbers if both exist
			const commit1 = extractCommitNumber(v1);
			const commit2 = extractCommitNumber(v2);

			if (commit1 !== null && commit2 !== null) {
				return commit1 - commit2;
			}

			return 0;
		};

		const isDevVersion = (v: string) => {
			const base = transformVersion(v);
			return base?.startsWith('127');
		};

		const newVersionIsEligible =
			noFirmwareInstalled ||
			installFailed ||
			!version ||
			(isDevVersion(newVersion)
				? compareVersions(newVersion, version) !== 0 // Dev: show if different OR commit is greater
				: compareVersions(newVersion, version) > 0); // Regular: only if strictly greater

		const buttonText = {
			[FirmwareStatus.UPGRADE]: translate('upgradeVersion'),
			[FirmwareStatus.UPGRADING]: translate('upgradingVersion', {
				version: transformVersion(newVersion),
			}),
			[FirmwareStatus.INSTALL]: translate('installVersion', {
				version: transformVersion(newVersion),
			}),
			[FirmwareStatus.INSTALLING]: translate('installingVersion', {
				version: transformVersion(newVersion),
			}),
			[FirmwareStatus.UPGRADE_FAILED]: translate('upgradeAgain'),
			[FirmwareStatus.INSTALL_FAILED]: translate('installAgain'),
		};

		const tooltipText = {
			[FirmwareStatus.UPGRADE_FAILED]: translate('tooltip.upgradeError'),
			[FirmwareStatus.INSTALL_FAILED]: translate('tooltip.installError'),
		};

		const setTextStyles = (status: DeviceStatusEnum) =>
			clsx({
				[styles.soft]: status === DeviceStatusEnum.Offline,
				[styles.velvet]: status === DeviceStatusEnum.Recording,
			});

		return (
			<div className={styles.upgrade}>
				{firmwareStatus !== FirmwareStatus.INSTALL &&
					firmwareStatus !== FirmwareStatus.INSTALL_FAILED &&
					(version?.includes('commit') ? (
						<Tooltip title={`v.${version}`} className={styles.tooltipHidden}>
							<Text
								data-testid="camera_firmware"
								className={!noTextStyle && setTextStyles(deviceStatus)}
							>
								{!actionInProgress &&
									!installFailed &&
									`v.${simplifiedVersion}`}
							</Text>
						</Tooltip>
					) : (
						<Text
							data-testid="camera_firmware"
							className={!noTextStyle && setTextStyles(deviceStatus)}
						>
							{!noFirmwareInstalled && `v.${simplifiedVersion}`}
						</Text>
					))}
				{firmwareStatus !== FirmwareStatus.LATEST &&
					newVersion &&
					!isServerless &&
					(newVersionIsEligible || isCameraUnavailable) && (
						<Button
							type="link"
							{...firmwareButtonProps}
							{...(!disable && { onClick: action })}
						>
							<Tooltip
								title={`v.${newVersion}`}
								className={styles.tooltipHidden}
							>
								{buttonText[firmwareStatus]}
								{firmwareActionAvailable &&
									!(upgradeFailed || installFailed) && (
										<Icon
											component={IconNames.DOWNLOAD}
											size={24}
											className="ml-1"
										/>
									)}
							</Tooltip>
							{(upgradeFailed || installFailed) && (
								<Tooltip title={tooltipText[firmwareStatus]}>
									<Icon
										component={IconNames.ALERT}
										size={24}
										className="ml-1"
									/>
								</Tooltip>
							)}
							{(isCameraUnavailable || actionInProgress) && (
								<Spin
									className="ml-1"
									indicator={<LoadingOutlined size={24} />}
								/>
							)}
						</Button>
					)}
			</div>
		);
	}
);
FirmwareField.displayName = 'FirmwareField';
export default FirmwareField;
